import React from 'react';
import {
  CalendarOutlined,
  FlagOutlined,
  LineChartOutlined,
  TeamOutlined,
  WarningOutlined
} from '@ant-design/icons';
import styled from 'styled-components';

import CardRow from 'containers/Overwatch/components/CardRow';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { useOrbitContext } from '../../OrbitContext';
import OrbitMarkReply from '../../components/OrbitMarkReply';
import { TOrbit } from '../../i18n/TModules';
import { getContextualStarters, isStarterAllowed, OrbitPromptStarter } from '../../promptStarters';
import { orbitColorsFor } from '../../styles';
import useOrbitStarters from '../../useOrbitStarters';
import { ORBIT_PAGE_MOCK } from '../orbitPageMock';
import OrbitPageComposer from './OrbitPageComposer';
import { pg, PlainButton, tint, type } from './pageStyles';

// `pg.link` is a `var()` reference with no channels for `tint`, so the 14% fills are taken from the
// dark reading the frame is drawn in.
const ACTION_HEX = orbitColorsFor('dark').action;

const PAGE_GLOBAL_STARTERS: OrbitPromptStarter[] = [
  'changed',
  'attention',
  'trend',
  'why',
  'next'
].map(id => ({ id: `page.${id}`, labelKey: `${TOrbit}.page.starters.${id}` }));

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: -8px 0 0 -8px;

  > * {
    margin: 8px 0 0 8px;
  }
`;

// 6114:4166's active chip.
const Chip = styled(PlainButton)`
  display: inline-flex;
  align-items: center;
  height: 29px;
  padding: 0 12px;
  border: 1px solid ${pg.link};
  border-radius: 999px;
  background: ${tint(ACTION_HEX, 0.14)};
  color: ${pg.link};
  white-space: nowrap;
  ${type('helping')}

  > * + * {
    margin-left: 6px;
  }
`;

// 6114:4185, top to bottom: mark, title 14.6 below it, subtitle 3.8, composer 20, then the cards
// 38 below the composer.
const Hero = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 1068px;
  margin: 0 auto;
  text-align: center;
`;

const HeroTitle = styled.h1`
  margin: 14.6px 0 0;
  color: ${pg.text};
  ${type('hero', 700)}
  letter-spacing: -1.02px;
`;

const HeroSubtitle = styled.p`
  margin: 3.8px 0 0;
  color: ${pg.muted};
  ${type('conflictBody')}
`;

const HeroComposer = styled.div`
  width: 100%;
  margin-top: 20px;
`;

// 6114:4208 and 6456:11887 share a card: 118.3 tall, r12, its content 18.1 / 13.8 in, 10 apart.
const Tile = styled(PlainButton)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  min-height: 118.3px;
  padding: 18.1px 13.8px;
  border: 1px solid ${pg.border};
  border-radius: 12px;
  background: ${pg.card};
  color: ${pg.text};

  > * + * {
    margin-top: 10px;
  }
`;

const Starter = styled(Tile)`
  ${type('conflictBody', 700)}
`;

type StarterTone = 'blue' | 'green' | 'red' | 'violet';

const STARTER_TONES: StarterTone[] = ['blue', 'green', 'red', 'violet'];

// Hex for the same reason: `StarterIcon` tints the tone.
const STARTER_TONE: Record<StarterTone, string> = {
  blue: ACTION_HEX,
  green: pg.success,
  red: pg.danger,
  violet: orbitColorsFor('dark').accent
};

const STARTER_ICON: Record<StarterTone, React.ComponentType> = {
  blue: LineChartOutlined,
  green: TeamOutlined,
  red: FlagOutlined,
  violet: WarningOutlined
};

// 6114:4209: a 34 tile at r9 around an 18 glyph.
const StarterIcon = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 9px;
  background: ${({ $color }) => tint($color, 0.14)};
  color: ${({ $color }) => $color};
  font-size: 18px;
`;

const StarterRow = styled.div`
  width: 100%;
  margin-top: 38px;
  text-align: left;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
`;

const Center = styled.div`
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  padding: 48px 0;
`;

interface IOrbitWelcomeProps {
  onOpenContext: () => void;
  composerRef: React.Ref<HTMLInputElement>;
}

const OrbitWelcome: React.FC<IOrbitWelcomeProps> = ({ onOpenContext, composerRef }) => {
  const { welcome } = ORBIT_PAGE_MOCK;
  const { t } = useMultiLanguageTranslation();
  const { ask } = useOrbitContext();
  const { screen, cards, grants } = useOrbitStarters();
  // FR §4.12: the four global starters, then any contextual ones the current authorized screen
  // supplies, filtered by the same grants the panel uses; a starter never grants permission.
  const starters = [
    ...PAGE_GLOBAL_STARTERS,
    ...getContextualStarters(screen, cards).filter(starter => isStarterAllowed(starter, grants))
  ];
  return (
    <Body data-testid="orbit-page-welcome">
      <Chips aria-label="Analysis context">
        {welcome.chips.map(chip => (
          <Chip key={chip.label} type="button" onClick={onOpenContext}>
            <CalendarOutlined aria-hidden="true" />
            <span>{chip.label}</span>
          </Chip>
        ))}
      </Chips>
      <Center>
        <Hero>
          <OrbitMarkReply width={29} height={30} />
          <HeroTitle>{welcome.title}</HeroTitle>
          <HeroSubtitle>{welcome.subtitle}</HeroSubtitle>
          <HeroComposer>
            <OrbitPageComposer inputRef={composerRef} pickerPlacement="below" />
          </HeroComposer>
          <StarterRow>
            <CardRow $columns={4} $min={200}>
              {starters.map((starter, index) => {
                const tone = STARTER_TONES[index % STARTER_TONES.length];
                const Icon = STARTER_ICON[tone];
                const text = t(starter.labelKey);
                return (
                  <Starter
                    key={starter.id}
                    type="button"
                    onClick={() => ask(text)}
                    data-testid="orbit-page-starter"
                  >
                    <StarterIcon $color={STARTER_TONE[tone]} aria-hidden="true">
                      <Icon />
                    </StarterIcon>
                    <span>{text}</span>
                  </Starter>
                );
              })}
            </CardRow>
          </StarterRow>
        </Hero>
      </Center>
    </Body>
  );
};

export default OrbitWelcome;
